import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepReturnTo } from '../components/common/returnPath';
import api from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import LoadingSpinner from '../components/LoadingSpinner';
import LLMSuggest from '../components/common/LLMSuggest';
import SettingsLayout, {
  SettingsSection,
} from '../components/settings/SettingsLayout';
import KnowledgeBriefPanel from '../components/KnowledgeBriefPanel';
import KnowledgeSuggestLauncher from '../components/KnowledgeSuggestLauncher';
import DocumentsPanel from '../components/DocumentsPanel';
import ImagesPanel from '../components/ImagesPanel';
import GrowthAgentPanel from '../components/GrowthAgentPanel';
import type { BrandProfile, Project, ResearchSourcePolicy } from '../types';

/**
 * Which evidence the page builder may cite. The help text is deliberately
 * concrete about consequences — picking "knowledge base only" silently turns
 * off SERP crawling and live web access for every page in the project, and a
 * user who doesn't expect that reads the resulting thin page as a bug.
 */
const RESEARCH_SOURCE_OPTIONS: {
  value: ResearchSourcePolicy;
  label: string;
  help: string;
}[] = [
  {
    value: 'ALL',
    label: 'Competitor pages + knowledge base (default)',
    help: 'Pages cite facts mined from the pages ranking for the keyword and from your own knowledge base and uploaded documents.',
  },
  {
    value: 'KNOWLEDGE_ONLY',
    label: 'Knowledge base only',
    help: "Pages cite only your knowledge proposals and uploaded documents. No SERP is crawled and the writer has no web access, so anything your corpus doesn't cover simply won't appear. Best when every claim has to trace back to material you supplied.",
  },
  {
    value: 'CRAWLED_ONLY',
    label: 'Competitor pages only',
    help: 'Pages cite only facts mined from the pages ranking for the keyword. Your knowledge base is not used as evidence, though its brand voice and domain rules still apply.',
  },
];

type Section =
  | 'general'
  | 'knowledge'
  | 'documents'
  | 'images'
  | 'growth-agent'
  | 'danger';

const SECTIONS: SettingsSection[] = [
  { key: 'general', label: 'General' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'documents', label: 'Documents' },
  { key: 'images', label: 'Images' },
  { key: 'growth-agent', label: 'Growth agent' },
  { key: 'danger', label: 'Danger zone', danger: true },
];

export default function ProjectSettings() {
  const { projectId: idParam, section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshProjects } = useProject();
  const projectId = Number(idParam);
  // Old "plan" tab moved to a slideout on the project page; redirect any
  // bookmarked /settings/plan URL so it doesn't land on a blank page.
  useEffect(() => {
    if (sectionParam === 'plan') {
      navigate(`/projects/${idParam}/settings/general`, { replace: true });
    }
  }, [sectionParam, idParam, navigate]);
  const section = (sectionParam || 'general') as Section;

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    try {
      const p = await api.getProject(projectId);
      setProject(p);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load subject');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // The section rail must not eat the way out. `section` is a route segment, so
  // navigating to it drops the query string — and with it the `?from=` that the
  // header's back button is pointing at. Looking around the sections before
  // heading back is the normal case, not a reason to strand someone.
  const handleSectionChange = (key: string) => {
    const qs = keepReturnTo(searchParams, new URLSearchParams()).toString();
    navigate(`/projects/${projectId}/settings/${key}` + (qs ? `?${qs}` : ''));
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !project) {
    return (
      <div className="p-6 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        <p>{error ?? 'Subject not found'}</p>
        <Link to="/subjects" className="inline-block mt-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline">
          ← Back to projects
        </Link>
      </div>
    );
  }

  return (
    <SettingsLayout
      title={project.name}
      scopeLabel="Subject"
      breadcrumb={[
        { label: 'Subjects', to: '/subjects' },
        { label: project.name, to: `/subjects/${projectId}` },
        { label: 'Settings' },
      ]}
      backTo={`/subjects/${projectId}`}
      backLabel="Back to subject"
      sections={SECTIONS}
      activeSection={section}
      onSectionChange={handleSectionChange}
    >
      {section === 'general' && (
        <GeneralSection
          project={project}
          onSaved={() => {
            fetchProject();
            refreshProjects();
          }}
        />
      )}
      {section === 'knowledge' && (
        <>
          <KnowledgeSuggestLauncher
            scope="project"
            scopeId={projectId}
            scopeName={project.name}
          />
          <KnowledgeBriefPanel
            scope="project"
            scopeId={projectId}
            scopeName={project.name}
          />
        </>
      )}
      {section === 'documents' && (
        <DocumentsPanel project_id={projectId} label="Subject Documents" />
      )}
      {section === 'images' && (
        <ImagesPanel project_id={projectId} label="Subject Images" />
      )}
      {section === 'growth-agent' && (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded p-4 text-sm text-blue-900 dark:text-blue-200">
            <h3 className="font-semibold mb-1">What this agent does</h3>
            <p>
              The Growth agent drives this project's SEO content lifecycle
              automatically: it reviews pending keyword recommendations,
              shapes the category / topic structure (when permitted), queues
              new page builds for high-opportunity keywords, and reviews
              draft pages. It reads the Name, Context, Niche description,
              and Seed labels from <strong>General</strong> to decide what fits.
            </p>
            <p className="mt-2">
              Enable the agent to allow scheduled runs at the interval
              below; use <strong>Run Now</strong> for an immediate pass.
              Each capability below is opt-in - the agent only sees the
              tools you've enabled.
            </p>
          </div>
          <GrowthAgentPanel
            projectId={projectId}
            projectName={project.name}
            projectDescription={project.description ?? undefined}
          />
        </div>
      )}
      {section === 'danger' && (
        <div className="space-y-6">
          <MergeDuplicatesSection project={project} />
          <ClearUnusedStructureSection project={project} />
          <DangerSection
            project={project}
            onDeleted={() => {
              refreshProjects();
              navigate('/subjects');
            }}
          />
        </div>
      )}
    </SettingsLayout>
  );
}

// -----------------------------------------------------------------------------
// General — basic edit form (name, description, active)
// -----------------------------------------------------------------------------

function GeneralSection({
  project,
  onSaved,
}: {
  project: Project;
  onSaved: () => void;
}) {
  // Project fields
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [isActive, setIsActive] = useState(project.is_active);
  const [imageGenEnabled, setImageGenEnabled] = useState(
    project.image_generation_enabled ?? false
  );
  const [verifyCitations, setVerifyCitations] = useState(
    project.verify_citation_faithfulness ?? true
  );
  const [researchSources, setResearchSources] = useState<ResearchSourcePolicy>(
    project.research_sources ?? 'ALL'
  );
  // Which brand this project's shared artifacts render in. Null = the account
  // default, which is what most people want; the override exists because one
  // account may cover several unrelated subjects for several end clients.
  const [autoReady, setAutoReady] = useState(
    project.auto_ready_on_clean_build ?? false
  );
  const [brandId, setBrandId] = useState<number | null>(
    project.brand_profile_id ?? null
  );
  const [brands, setBrands] = useState<BrandProfile[]>([]);
  useEffect(() => {
    api.listBrandProfiles().then(setBrands).catch(() => setBrands([]));
  }, []);

  // GrowthAgentConfig-backed fields (stored separately; saved in parallel).
  const [nicheDescription, setNicheDescription] = useState('');
  const [seedLabels, setSeedLabels] = useState<string[]>([]);
  const [seedLabelInput, setSeedLabelInput] = useState('');
  // Snapshot of what the API gave us, so `dirty` detection works.
  const baselineRef = useRef<{ niche: string; labels: string[] }>({
    niche: '',
    labels: [],
  });

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLabels, setSavingLabels] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await api.getGrowthAgentConfig(project.id);
        const niche = cfg.niche_description ?? '';
        const labels = cfg.seed_labels ?? [];
        setNicheDescription(niche);
        setSeedLabels(labels);
        baselineRef.current = { niche, labels: [...labels] };
      } catch (e) {
        /* best-effort — niche/labels are optional */
      } finally {
        setLoadingConfig(false);
      }
    })();
  }, [project.id]);

  const seedLabelsChanged =
    seedLabels.length !== baselineRef.current.labels.length ||
    seedLabels.some((l, i) => l !== baselineRef.current.labels[i]);

  const dirty =
    name !== project.name ||
    description !== (project.description ?? '') ||
    isActive !== project.is_active ||
    imageGenEnabled !== (project.image_generation_enabled ?? false) ||
    verifyCitations !== (project.verify_citation_faithfulness ?? true) ||
    researchSources !== (project.research_sources ?? 'ALL') ||
    brandId !== (project.brand_profile_id ?? null) ||
    autoReady !== (project.auto_ready_on_clean_build ?? false) ||
    nicheDescription !== baselineRef.current.niche ||
    seedLabelsChanged;

  // Seed labels persist immediately (on Enter / suggestion / remove) — no
  // separate Save press required, matching the topic Labels editor.
  const persistSeedLabels = async (next: string[]) => {
    setSavingLabels(true);
    setError(null);
    try {
      await api.updateGrowthAgentConfig(project.id, { seed_labels: next });
      setSeedLabels(next);
      baselineRef.current = { ...baselineRef.current, labels: [...next] };
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save seed labels');
    } finally {
      setSavingLabels(false);
    }
  };

  const addSeedLabel = (rawValue?: string) => {
    const candidate = (rawValue ?? seedLabelInput).trim().toLowerCase();
    setSeedLabelInput('');
    if (!candidate) return;
    if (seedLabels.includes(candidate)) return;
    persistSeedLabels([...seedLabels, candidate]);
  };

  const removeSeedLabel = (label: string) => {
    persistSeedLabels(seedLabels.filter((l) => l !== label));
  };

  const llmCtx: Record<string, string> = {};
  if (name) llmCtx.project_name = name;
  if (description) llmCtx.project_context = description;

  const handleSave = async () => {
    // flush any pending text in the label input into the array
    const pending = seedLabelInput.trim().toLowerCase();
    let labelsToSave = seedLabels;
    if (pending && !labelsToSave.includes(pending)) {
      labelsToSave = [...labelsToSave, pending];
      setSeedLabels(labelsToSave);
      setSeedLabelInput('');
    }

    setSaving(true);
    setError(null);
    const results = await Promise.allSettled([
      api.updateProject(project.id, {
        name,
        description,
        is_active: isActive,
        image_generation_enabled: imageGenEnabled,
        verify_citation_faithfulness: verifyCitations,
        research_sources: researchSources,
        brand_profile_id: brandId,
        auto_ready_on_clean_build: autoReady,
      }),
      api.updateGrowthAgentConfig(project.id, {
        niche_description: nicheDescription || null,
        seed_labels: labelsToSave,
      }),
    ]);
    const failures = results
      .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      .map((r) => (r.reason instanceof Error ? r.reason.message : String(r.reason)));
    setSaving(false);
    if (failures.length) {
      setError(failures.join('; '));
      return;
    }
    baselineRef.current = { niche: nicheDescription, labels: [...labelsToSave] };
    onSaved();
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">General</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Project identity and the attributes downstream agents read as context.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        {!name.trim() && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">Subject name is required.</p>
        )}
        <div className="relative">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full px-3 py-2 border rounded text-sm ${!name.trim() ? 'border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30' : 'border-gray-300 dark:border-gray-600'}`}
          />
          <LLMSuggest
            variant="typeahead"
            context="project_name"
            value={name}
            additionalContext={{
              ...(description ? { project_context: description } : {}),
              ...(nicheDescription ? { niche_description: nicheDescription } : {}),
              ...(seedLabels.length ? { seed_labels: seedLabels.join(', ') } : {}),
            }}
            onSelect={(s) => setName(s)}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Context <span className="text-red-500">*</span>
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            description of the project - used as LLM context
          </span>
        </label>
        {!description.trim() && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">Subject context is required - agents use this to understand the project.</p>
        )}
        <div className="relative">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 border rounded text-sm ${!description.trim() ? 'border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30' : 'border-gray-300 dark:border-gray-600'}`}
          />
          <LLMSuggest
            variant="typeahead"
            context="project_description"
            value={description}
            additionalContext={{
              ...(name ? { project_name: name } : {}),
              ...(nicheDescription ? { niche_description: nicheDescription } : {}),
              ...(seedLabels.length ? { seed_labels: seedLabels.join(', ') } : {}),
            }}
            onSelect={(s) => setDescription(s)}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Niche description
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            the angle / audience the project is built around
          </span>
        </label>
        <div className="relative">
          <textarea
            value={nicheDescription}
            onChange={(e) => setNicheDescription(e.target.value)}
            rows={3}
            placeholder={loadingConfig ? 'Loading…' : "Describe the project's niche or focus area…"}
            disabled={loadingConfig}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:bg-gray-50 dark:disabled:bg-gray-800 dark:bg-gray-800"
          />
          <LLMSuggest
            variant="typeahead"
            context="niche_description"
            value={nicheDescription}
            additionalContext={{
              ...llmCtx,
              ...(seedLabels.length ? { seed_labels: seedLabels.join(', ') } : {}),
            }}
            onSelect={(s) => setNicheDescription(s)}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Seed labels
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            project-level tags used as agent context and discovery seeds
          </span>
          {savingLabels && (
            <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">Saving…</span>
          )}
        </label>
        <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
          {loadingConfig ? (
            <span className="text-xs text-gray-400 dark:text-gray-500">Loading…</span>
          ) : seedLabels.length === 0 ? (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">No labels yet</span>
          ) : (
            seedLabels.map((label) => (
              <span
                key={label}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-sm bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded"
              >
                {label}
                <button
                  type="button"
                  onClick={() => removeSeedLabel(label)}
                  className="icon-btn text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300"
                  aria-label={`Remove seed label ${label}`}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <div className="relative">
          <input
            type="text"
            value={seedLabelInput}
            onChange={(e) => setSeedLabelInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSeedLabel();
              }
            }}
            placeholder="Type a label and press Enter"
            disabled={loadingConfig}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:bg-gray-50 dark:disabled:bg-gray-800 dark:bg-gray-800"
          />
          <LLMSuggest
            variant="typeahead"
            context="seed_label"
            value={seedLabelInput}
            multiSelect
            additionalContext={{
              ...llmCtx,
              ...(nicheDescription ? { niche_description: nicheDescription } : {}),
              ...(seedLabels.length ? { existing_labels: seedLabels.join(', ') } : {}),
            }}
            onSelect={(s) => addSeedLabel(s)}
          />
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className={`px-3 py-1.5 text-sm rounded ${
            isActive
              ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          {isActive ? 'Subject Active' : 'Subject Inactive'}
        </button>
      </div>

      <div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={imageGenEnabled}
            onChange={(e) => setImageGenEnabled(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500"
          />
          <span className="text-gray-700 dark:text-gray-300">Enable AI image generation</span>
        </label>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Lets the page builder generate illustrations for this project's pages.
          Requires an image-generation model (Settings → AI Models) and an
          image storage bucket (Settings → Image Storage).
        </p>
      </div>

      <div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={verifyCitations}
            onChange={(e) => setVerifyCitations(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500"
          />
          <span className="text-gray-700 dark:text-gray-300">Verify citation faithfulness</span>
        </label>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          At build time, an LLM checks each cited claim against its source fact and
          fixes unfaithful ones with a section-scoped revision. Invented citations
          are always caught regardless of this setting; turning it off just skips
          the extra faithfulness pass.
        </p>
      </div>

      <div>
        <label
          htmlFor="research-sources"
          className="block text-sm text-gray-700 dark:text-gray-300"
        >
          Research sources
        </label>
        <select
          id="research-sources"
          value={researchSources}
          onChange={(e) =>
            setResearchSources(e.target.value as ResearchSourcePolicy)
          }
          className="mt-1 block w-full max-w-md rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-indigo-500 focus:border-indigo-500"
        >
          {RESEARCH_SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {RESEARCH_SOURCE_OPTIONS.find((o) => o.value === researchSources)?.help}
        </p>
      </div>

      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoReady}
            onChange={(e) => setAutoReady(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
          />
          <span>
            <span className="block text-sm text-gray-700 dark:text-gray-300">
              Mark clean builds ready to deploy
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              A finished build that passes the deploy checks becomes “Ready to deploy” on
              its own, instead of waiting for someone to set it by hand one page at a
              time. Nothing is published - you still press Deploy. Leave this off if a
              person is meant to read every draft first.
            </span>
          </span>
        </label>
      </div>

      <div>
        <label htmlFor="brand-profile" className="block text-sm text-gray-700 dark:text-gray-300">
          Brand
        </label>
        <select
          id="brand-profile"
          value={brandId ?? ''}
          onChange={(e) => setBrandId(e.target.value ? Number(e.target.value) : null)}
          className="mt-1 block w-full max-w-md rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="">
            Account default
            {brands.find((b) => b.is_default) ? ` (${brands.find((b) => b.is_default)!.name})` : ''}
          </option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          What a reader sees on artifacts shared from this project - briefings, pages, reports.{' '}
          <Link to="/settings?tab=brand" className="underline hover:no-underline">
            Manage brands
          </Link>
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={handleSave}
          disabled={!dirty || saving || loadingConfig || !name.trim() || !description.trim()}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Danger zone — merge duplicate categories / topics / keywords
// -----------------------------------------------------------------------------

function MergeDuplicatesSection({ project }: { project: Project }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const handleMerge = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.mergeDuplicateStructure(project.id);
      const parts: string[] = [];
      if (res.categories_merged)
        parts.push(
          `${res.categories_merged} categor${res.categories_merged === 1 ? 'y' : 'ies'}`,
        );
      if (res.topics_merged)
        parts.push(`${res.topics_merged} topic${res.topics_merged === 1 ? '' : 's'}`);
      if (res.keywords_merged)
        parts.push(
          `${res.keywords_merged} keyword${res.keywords_merged === 1 ? '' : 's'}`,
        );
      setResult(
        parts.length
          ? `Merged ${parts.join(', ')} into surviving nodes.`
          : 'No duplicates found - nothing to merge.',
      );
      setConfirming(false);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to merge');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-red-700 dark:text-red-300">Merge duplicates</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Finds nodes with the same name ignoring case and whitespace and folds
          them together, top-down: duplicate categories first (their topics move
          into the survivor), then duplicate topics within each category (their
          keywords move in), then duplicate keywords within each topic. Pages,
          documents, images, and ideas follow their keyword to the survivor; the
          active (or pinned) node is kept. This cannot be undone.
        </p>
      </div>
      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}
      {result && (
        <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2">
          {result}
        </div>
      )}
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            onClick={handleMerge}
            disabled={busy}
            className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Merging…' : 'Yes, merge duplicates'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm rounded hover:bg-red-50 dark:hover:bg-red-900/30"
        >
          Merge duplicates
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Danger zone — clear unused structure
// -----------------------------------------------------------------------------

function ClearUnusedStructureSection({ project }: { project: Project }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const handleClear = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.clearUnusedStructure(project.id);
      const parts: string[] = [];
      if (res.seed_keywords_removed)
        parts.push(
          `${res.seed_keywords_removed} seed keyword${res.seed_keywords_removed === 1 ? '' : 's'}`,
        );
      if (res.topics_removed)
        parts.push(`${res.topics_removed} topic${res.topics_removed === 1 ? '' : 's'}`);
      if (res.categories_removed)
        parts.push(
          `${res.categories_removed} categor${res.categories_removed === 1 ? 'y' : 'ies'}`,
        );
      setResult(
        parts.length
          ? `Removed ${parts.join(', ')} from the project.`
          : 'Nothing to remove - every node already has at least one page.',
      );
      setConfirming(false);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to clear');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-red-700 dark:text-red-300">
          Clear unused structure
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Permanently deletes from this project: seed keywords with no built
          page, topics with no remaining seed keywords, and categories with no
          remaining topics. Anything with at least one page survives. This
          cannot be undone.
        </p>
      </div>
      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}
      {result && (
        <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2">
          {result}
        </div>
      )}
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            onClick={handleClear}
            disabled={busy}
            className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Clearing…' : 'Yes, clear unused structure'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm rounded hover:bg-red-50 dark:hover:bg-red-900/30"
        >
          Clear unused structure
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Danger zone — delete
// -----------------------------------------------------------------------------

function DangerSection({
  project,
  onDeleted,
}: {
  project: Project;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteProject(project.id);
      onDeleted();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Deleting this project removes all of its categories, topics, keywords,
          pages, and knowledge briefs. This cannot be undone.
        </p>
      </div>
      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            disabled={busy}
            className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Deleting…' : `Yes, delete "${project.name}"`}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm rounded hover:bg-red-50 dark:hover:bg-red-900/30"
        >
          Delete project
        </button>
      )}
    </div>
  );
}
